import { IconButton } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import FullScreenIcon from '@material-ui/icons/Fullscreen';
import FullScreenExitIcon from '@material-ui/icons/FullscreenExitOutlined';
import memoize from 'memoize-one';
import React, { useEffect, useState as useStateHook } from 'react';
import { GoogleMap, Marker, withGoogleMap, withScriptjs } from 'react-google-maps';
import { Translate } from 'react-localize-redux';
import {
  compose,
  defaultProps,
  setDisplayName,
  UnpackICEWP,
  withHandlers,
  withState,
} from 'recompose';
import { enqueueTranslateNotification, removeNotification } from '../../../actions/context.actions';
import { updateCoordinates } from '../../../actions/store.actions';
import markerSvg from '../../../assets/images/marker.svg';
import { GMAP_API_KEY } from '../../../constants/map.constant';
import { Button } from '../../../ui/Button';
import withStoreByIdData, { WithStoreByIdData } from '../withStoreByIdData';
import SearchByAddress from './SearchByAddress';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      position: 'relative',
    },
    toolbox: {
      position: 'absolute',
      right: '12px',
      bottom: '12px',
      height: '52px',
      borderRadius: '3px',
      backgroundColor: ' rgba(255, 255, 255, 0.8)',
      display: 'flex',
      justifyContent: 'space-around',
      alignItems: 'center',
      padding: '8px',
      '& > button': {
        color: '#05149e',
      },
    },
    search: {
      position: 'absolute',
      bottom: '12px',
      left: '12px',
      backgroundColor: ' rgba(255, 255, 255, 0.8)',
    },
  });

type MapDefaultProps = typeof mapDefaultProps;
const mapDefaultProps = {
  googleMapURL: `https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry,drawing,places${
    GMAP_API_KEY ? '&key=' + GMAP_API_KEY : ''
  }`, // &language=ja&region=JP
  loadingElement: <div style={{ height: `100%` }} />,
  containerElement: <div style={{ height: `100%` /*minHeight: '400px'*/ }} />,
  mapElement: <div style={{ height: `100%` }} />,
  options: {
    minZoom: 1,
    fullscreenControl: false,
    streetViewControl: false,
    mapTypeControl: false,
    zoomControl: true,
    scaleControl: false,
    panControl: false,
    rotateControl: false,
    // styles: mapsStyles
  },
  mapRef: React.createRef<GoogleMap>(),
};

const getMapCenter = (storeByIdData: Props['storeByIdData']) => {
  const storeCoords = storeByIdData && storeByIdData.Address && storeByIdData.Address.Coordinates;
  if (storeCoords && storeCoords.Latitude && storeCoords.Longitude) {
    return {
      lat: storeCoords.Latitude,
      lng: storeCoords.Longitude,
    };
  }
  return;
};

type State = UnpackICEWP<typeof useState>;
const useState = withState('state', 'setState', ({ storeByIdData, defaultMapCenter }: Props) => {
  const center = getMapCenter(storeByIdData) || defaultMapCenter;

  if (center.lat === 0 && center.lng === 0 && defaultMapCenter) {
    return {
      center: { ...defaultMapCenter },
      position: { ...defaultMapCenter },
      dirty: true,
    };
  }

  return {
    center,
    position: { ...center },
    dirty: false,
  };
});

const handlers = () => {
  const getMapCenterCache = memoize(getMapCenter);

  return {
    getMapCenter: ({ storeByIdData }: Props) => () => getMapCenterCache(storeByIdData),
    handleMapCenterChange: ({
      state: { position, dirty },
      mapRef,
      setState,
    }: Props & State) => () => {
      setState({
        dirty,
        position,
        center: mapRef.current!.getCenter().toJSON(),
      });
    },
    handleMarkerDragEnd: ({ setState }: Props & State) => (e: google.maps.MouseEvent) => {
      setState({
        center: e.latLng.toJSON(),
        position: e.latLng.toJSON(),
        dirty: true,
      });
    },
    handleClick: ({ setState, state: { center } }: Props & State) => (
      e: google.maps.MouseEvent
    ) => {
      setState({
        center,
        position: e.latLng.toJSON(),
        dirty: true,
      });
    },
    handleCancel: ({ storeByIdData, setState, defaultMapCenter }: Props & State) => () => {
      const center = getMapCenterCache(storeByIdData) || defaultMapCenter;
      setState({
        center,
        position: { ...center },
        dirty: false,
      });
    },
    handleSave: ({ storeByIdData, state, setState, dispatch }: Props & State) => () => {
      const { StoreId, StoreGroupId } = storeByIdData!;
      dispatch(enqueueTranslateNotification('Auto_saving'));
      dispatch(
        updateCoordinates(StoreId, StoreGroupId, {
          Latitude: state.position.lat,
          Longitude: state.position.lng,
        })
      )
        // @ts-ignore
        .then(() => {
          dispatch(removeNotification());
          setState({
            ...state,
            dirty: false,
          });
        })
        .catch((err) => {
          dispatch(removeNotification());
        });
    },
    handleSearch: ({ setState }: Props & State) => (res) => {
      setState({
        center: res,
        position: res,
        dirty: true,
      });
    },
  };
};
type Handlers = UnpackICEWP<typeof useHandlers>;
const useHandlers = withHandlers(handlers);

export interface IStoreLocationMapProps {
  storeId: number;
  defaultMapCenter: { lat: number; lng: number };
  disabled?: boolean;
}
type Props = IStoreLocationMapProps &
  MapDefaultProps &
  WithStyles<typeof styles> &
  WithStoreByIdData;
const StoreLocationMap = compose<Props & State & Handlers, IStoreLocationMapProps>(
  setDisplayName('StoreLocationMap'),
  withStyles(styles, {
    name: 'StoreLocationMap',
    // withTheme: true
  }),
  defaultProps(mapDefaultProps),
  withScriptjs,
  withGoogleMap,
  withStoreByIdData,
  useState,
  useHandlers
)(
  ({
    mapRef,
    options,
    handleMapCenterChange,
    handleMarkerDragEnd,
    handleClick,
    handleCancel,
    handleSave,
    handleSearch,
    state,
    classes,
    disabled,
  }) => {
    const [fullscreen, setFullscreen] = useStateHook(false);

    useEffect(() => {
      const listener = () => {
        const parentNode = mapRef.current && (mapRef.current.getDiv().parentNode as HTMLDivElement);

        if (parentNode && document.fullscreenElement === parentNode) {
          setFullscreen(true);
        } else {
          setFullscreen(false);
        }
      };

      document.addEventListener('fullscreenchange', listener);

      return () => {
        document.removeEventListener('fullscreenchange', listener);
      };
    }, []);

    const mapOptions = disabled ? { ...options, gestureHandling: 'none' } : options;

    return (
      <>
        <div className={classes.root}>
          <GoogleMap
            defaultZoom={16}
            ref={mapRef}
            onCenterChanged={disabled ? undefined : handleMapCenterChange}
            center={state.center}
            onClick={handleClick}
            // @ts-ignore
            defaultOptions={{
              ...mapOptions,
              zoomControlOptions: {
                position: google.maps.ControlPosition.TOP_RIGHT,
              },
            }}
          >
            <Marker
              position={state.position}
              icon={markerSvg}
              draggable={disabled ? false : true}
              onDragEnd={disabled ? undefined : handleMarkerDragEnd}
            />
          </GoogleMap>

          {!disabled && (
            <div className={classes.toolbox}>
              {state.dirty && (
                <>
                  <Button fdKey="Cancel" onClick={handleCancel}>
                    <Translate id="Cancel" />
                  </Button>
                  <Button fdKey="Save" onClick={handleSave}>
                    <Translate id="Save" />
                  </Button>
                </>
              )}
              <IconButton
                onClick={() => {
                  const parentNode =
                    mapRef.current && (mapRef.current.getDiv().parentNode as HTMLDivElement);

                  if (parentNode) {
                    if (document.fullscreenElement === parentNode) {
                      document.exitFullscreen();
                    } else {
                      parentNode.requestFullscreen();
                    }
                  }
                }}
              >
                {fullscreen ? <FullScreenExitIcon /> : <FullScreenIcon />}
              </IconButton>
            </div>
          )}
          <div className={classes.search}>
            <SearchByAddress onSearch={handleSearch} />
          </div>
        </div>
      </>
    );
  }
);

export default StoreLocationMap;
